import { Footer } from "@layouts/Footer";
import arrowIcon from "@assets/images/Frame-9.svg";
import { HeroBadge, useFeedbackModal, Reveal } from "@components/ui";
import { HeroSection, EmergencyCTASection } from "../../components";
import { CustomerFeedbackPage } from "@features/general";

const feedbackHeroImage = `data:image/svg+xml,${encodeURIComponent(`
<svg width="520" height="480" viewBox="0 0 520 480" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="60" y="60" width="400" height="360" rx="32" fill="white" fill-opacity="0.08"/>
  <rect x="100" y="120" width="320" height="240" rx="20" fill="white" fill-opacity="0.06" stroke="white" stroke-opacity="0.15" stroke-width="2"/>
  <rect x="130" y="155" width="120" height="12" rx="6" fill="white" fill-opacity="0.2"/>
  <rect x="130" y="180" width="260" height="32" rx="10" fill="white" fill-opacity="0.06" stroke="white" stroke-opacity="0.12" stroke-width="1.5"/>
  <rect x="130" y="230" width="120" height="12" rx="6" fill="white" fill-opacity="0.2"/>
  <rect x="130" y="255" width="260" height="32" rx="10" fill="white" fill-opacity="0.06" stroke="white" stroke-opacity="0.12" stroke-width="1.5"/>
  <g transform="translate(130, 310)">
    <polygon points="12,0 15,8 24,9 17,15 19,24 12,19 5,24 7,15 0,9 9,8" fill="#F5B731"/>
    <polygon points="42,0 45,8 54,9 47,15 49,24 42,19 35,24 37,15 30,9 39,8" fill="#F5B731"/>
    <polygon points="72,0 75,8 84,9 77,15 79,24 72,19 65,24 67,15 60,9 69,8" fill="#F5B731"/>
    <polygon points="102,0 105,8 114,9 107,15 109,24 102,19 95,24 97,15 90,9 99,8" fill="#F5B731"/>
    <polygon points="132,0 135,8 144,9 137,15 139,24 132,19 125,24 127,15 120,9 129,8" fill="white" fill-opacity="0.2"/>
  </g>
  <rect x="130" y="345" width="140" height="36" rx="18" fill="#F24D2B"/>
  <rect x="155" y="357" width="60" height="10" rx="5" fill="white" fill-opacity="0.9"/>
  <g transform="translate(350, 80)">
    <rect width="110" height="70" rx="16" fill="white" fill-opacity="0.1" stroke="white" stroke-opacity="0.2" stroke-width="1.5"/>
    <rect x="16" y="18" width="50" height="8" rx="4" fill="white" fill-opacity="0.25"/>
    <rect x="16" y="34" width="78" height="8" rx="4" fill="white" fill-opacity="0.15"/>
    <rect x="16" y="50" width="35" height="8" rx="4" fill="white" fill-opacity="0.25"/>
  </g>
  <circle cx="80" cy="280" r="24" fill="white" fill-opacity="0.08" stroke="white" stroke-opacity="0.15" stroke-width="1.5"/>
  <polyline points="70,280 77,287 90,274" stroke="white" stroke-opacity="0.4" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
</svg>
`)}`;

export function CustomerFeedback() {
  const { openModal } = useFeedbackModal();

  return (
    <CustomerFeedbackPage
      hero={
        <HeroSection
          title="Customer Feedback & Service Survey"
          description="Your voice matters. Share your experience, report an issue, or rate our services — we're committed to improving every interaction."
          heroImage={feedbackHeroImage}
          cta={
            <button
              onClick={() => openModal()}
              className="flex items-center justify-center gap-2.5 py-3 pl-5 pr-2 bg-[#090d2c] rounded-[40px] hover:bg-[#3d405e] transition-colors"
            >
              <span className="text-[16px] md:text-[20px] lg:text-[24px] tracking-[-0.04em] text-white">
                Submit a Complaint
              </span>
              <img src={arrowIcon} alt="" className="w-8 md:w-10 rounded-full" />
            </button>
          }
          badge={
            <HeroBadge
              text="Every complaint is tracked with a unique reference number for full transparency."
              linkText="View Our Service Standards"
            />
          }
        />
      }
      feedbackCta={
        <section className="w-full bg-white overflow-hidden py-12 md:py-16 lg:py-[80px] px-5 md:px-10 lg:px-[180px]">
          <div className="w-full max-w-[1368px] mx-auto">
            <Reveal direction="up">
              <div className="rounded-[16px] md:rounded-[24px] bg-gradient-to-br from-[#122d63] to-[#0a1d42] p-8 md:p-12 lg:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-10">
                <div className="flex flex-col gap-3">
                  <h3 className="text-[22px] md:text-[32px] lg:text-[40px] tracking-[-0.04em] font-bold text-white font-albert-sans leading-[1.1]">
                    Ready to Share Your Feedback?
                  </h3>
                  <p className="max-w-[480px] text-[14px] md:text-[16px] lg:text-[18px] text-white/70 font-albert-sans leading-[1.5]">
                    It only takes a few minutes. Your feedback helps us deliver better service to every customer.
                  </p>
                </div>
                <button
                  onClick={() => openModal()}
                  className="shrink-0 flex items-center justify-center gap-2.5 py-3.5 md:py-4 pl-7 pr-5 md:pl-8 md:pr-6 bg-white hover:bg-[#f6f6f9] rounded-[40px] transition-all duration-200 group/btn"
                >
                  <span className="text-[16px] md:text-[18px] tracking-[-0.02em] font-semibold text-[#122d63] font-albert-sans whitespace-nowrap">
                    Open Feedback Form
                  </span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-[#122d63] transition-transform duration-300 group-hover/btn:translate-x-1">
                    <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </Reveal>
          </div>
        </section>
      }
      footer={
        <>
          <EmergencyCTASection
            title="Smell Gas? Act Now."
            subtitle={
              <>
                Call our 24/7 emergency hotline immediately.
                <br />
                Do not use electrical switches or open flames.
              </>
            }
          >
            <a
              href="tel:800-GAS"
              className="flex items-center gap-3 rounded-[40px] bg-white px-6 py-3 md:px-8 md:py-4 transition-colors hover:bg-gray-100"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f03813" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <span className="font-albert-sans text-[16px] md:text-[20px] font-semibold tracking-[-0.04em] text-[#0a0e2e]">
                800-GAS (427)
              </span>
            </a>
          </EmergencyCTASection>
          <Footer />
        </>
      }
    />
  );
}

export default CustomerFeedback;
